import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, CircularProgress, withTheme } from '@material-ui/core';
import { ErrorIcon } from '../../../icons';

import { useNotify } from '../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../hooks/useSwaggerClient';
import AdminHeader from '../../../components/atom/AdminHeader';
import { useHistory } from 'react-router';
import FormSection from '../../../components/atom/FormSection';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import AutocompleteSelect from '../../../components/atom/AutocompleteSelect';
import {
    onlyClientAutocomplete,
    chainAutocomplete,
    sourceGroupAutocomplete
} from '../../../autocomplete';

function SourceEditPage({ theme }) {
    const { sourceId } = useParams();
    const { notifyError, notifySuccess } = useNotify();
    const history = useHistory();

    // the first "false" disables this hook in here
    const hasId = !!sourceId;
    const [item, loading, error, setItem] = useSwaggerClient(
        hasId ? 'sources' : false,
        'getSource',
        {
            sourceId: parseInt(sourceId, 10)
        }
    );
    useEffect(() => {
        if (hasId) {
            return;
        }
        //setItem to default values
        setItem({
            name: '',
            companyId: null,
            chainId: null,
            sourceGroupId: null
        });
    }, [hasId]);

    const modifyItem = () => {
        doSwaggerCall('sources', hasId ? 'updateSource' : 'createSource', {
            sourceId: hasId ? parseInt(sourceId, 10) : null,
            source: {
                name: item.name,
                companyId: item.companyId,
                chainId: item.chainId,
                sourceGroupId: item.sourceGroupId
            }
        })
            .then(_res => {
                notifySuccess('Source group modified!');
                history.push(`/admin/sources`);
            })
            .catch(err => {
                console.log(err);
                notifyError(err?.response?.body?.message || 'Error while modifying source!');
            });
    };

    if (error) {
        return (
            <Grid container justifyContent="center">
                <h1>{error}</h1>
            </Grid>
        );
    }
    if ((loading && hasId) || item === null) {
        return (
            <Grid container justifyContent="center">
                <CircularProgress />
            </Grid>
        );
    }

    return (
        <Grid className="bg-white activity-edit">
            <form
                onSubmit={ev => {
                    ev.preventDefault();
                }}
            >
                <Grid container spacing={2}>
                    <AdminHeader title="Source" />
                    {!!error && (
                        <Grid
                            item
                            container
                            spacing={2}
                            xs={12}
                            style={{
                                color: theme.palette.error.main
                            }}
                            alignItems="center"
                        >
                            <Grid item>
                                <ErrorIcon color="error" />
                            </Grid>
                            <Grid item>
                                <span>{error.response.body.message}</span>
                            </Grid>
                        </Grid>
                    )}
                    <FormSection title="Generic information">
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                fullWidth
                                required
                                type="text"
                                value={item?.name}
                                onChange={ev => setItem({ ...item, name: ev.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <AutocompleteSelect
                                initCall={onlyClientAutocomplete.init}
                                autocompleteCall={onlyClientAutocomplete.search}
                                value={item?.companyId}
                                setValue={value => setItem({ ...item, companyId: value })}
                            >
                                <TextField label="Company" />
                            </AutocompleteSelect>
                        </Grid>
                        <Grid item xs={6}>
                            <AutocompleteSelect
                                initCall={chainAutocomplete.init}
                                autocompleteCall={chainAutocomplete.search}
                                value={item?.chainId}
                                setValue={value => setItem({ ...item, chainId: value })}
                            >
                                <TextField label="Chain" />
                            </AutocompleteSelect>
                        </Grid>
                        <Grid item xs={12}>
                            <AutocompleteSelect
                                initCall={sourceGroupAutocomplete.init}
                                autocompleteCall={sourceGroupAutocomplete.search}
                                value={item?.sourceGroupId}
                                setValue={value => setItem({ ...item, sourceGroupId: value })}
                            >
                                <TextField label="Source group" />
                            </AutocompleteSelect>
                        </Grid>
                    </FormSection>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={!item.name}
                            onClick={modifyItem}
                        >
                            Save
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => history.push(`/admin/sources`)}
                        >
                            Back
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Grid>
    );
}

export default withTheme(SourceEditPage);
